import { useState, useEffect, useRef } from 'react';
import {
  subscribeToProjectStatus,
  getProjectDocument,
  fetchPPTJson,
  fetchJsonWithRetry
} from '../project.service';
import toast from 'react-hot-toast';
import { PPTXTimeline } from 'schema';

const getTimelineUrlFromFirestore = async (projectId) => {
  const data = await getProjectDocument(projectId);
  if (data?.timeline) return data.timeline;
  throw new Error('Timeline URL not found in project');
};

export const useProjectStatus = (projectId) => {
  const [projectData, setProjectData] = useState(null);
  const [pptJson, setPptJson] = useState(null);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [parsedSlideCount, setParsedSlideCount] = useState(0);
  const [totalSlides, setTotalSlides] = useState(null);
  const lastParsedCountRef = useRef(0);
  const [timelineJson, setTimelineJson] = useState();
  const [timelineDetails, setTimelineDetails] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);
  const lastTimelineUrlRef = useRef(null);
  const lastJsonUrlRef = useRef(null);
  const prevStatusRef = useRef('');

  const loadTimelineOnceAvailable = async (timelineUrl) => {
    if (!timelineUrl) return;
    if (lastTimelineUrlRef.current === timelineUrl && timelineDetails && !timelineError) return;

    lastTimelineUrlRef.current = timelineUrl;
    setTimelineLoading(true);
    setTimelineError(null);

    try {
      const json = await fetchJsonWithRetry(timelineUrl, {
        attempts: 10,
        getFreshUrl: () => getTimelineUrlFromFirestore(projectId)
      });
      setTimelineJson(json);
      const parsed = PPTXTimeline.fromJSON(json);
      setTimelineDetails(parsed);
    } catch (e) {
      setTimelineError(e?.message || 'Failed to load timeline');
      lastTimelineUrlRef.current = null;
    } finally {
      setTimelineLoading(false);
    }
  };

  const reloadTimeline = async () => {
    if (!projectId) return;
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const timelineUrl = await getTimelineUrlFromFirestore(projectId);
      const json = await fetchJsonWithRetry(timelineUrl, {
        attempts: 10,
        getFreshUrl: () => getTimelineUrlFromFirestore(projectId)
      });
      lastTimelineUrlRef.current = timelineUrl;
      setTimelineJson(json);
      setTimelineDetails(PPTXTimeline.fromJSON(json));
    } catch (e) {
      setTimelineError(e?.message || 'Failed to reload timeline');
    } finally {
      setTimelineLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    let unsubscribe = null;

    setTimelineJson(undefined);
    setTimelineDetails(null);
    setTimelineError(null);
    setTimelineLoading(false);
    setStatus('');
    setProjectData(null);

    const init = async () => {
      try {
        setLoading(true);

        // Get initial project data
        const data = await getProjectDocument(projectId);
        if (data.timeline) {
          loadTimelineOnceAvailable(data.timeline);
        }
        setProjectData(data);
        setStatus(data.status);

        // Set total slides from metadata immediately if available
        const metadata = data.metadata || {};
        if (metadata.slideCount) {
          setTotalSlides(metadata.slideCount);
        }

        // Set parsed count if available
        if (data.parsedSlideCount) {
          setParsedSlideCount(data.parsedSlideCount);
          lastParsedCountRef.current = data.parsedSlideCount;
        }

        // If already done, fetch JSON immediately but continue to set up subscription
        if (data.status === 'EXTRACTION_DONE' && data.json) {
          const json = await fetchPPTJson(data.json);
          setPptJson(json);
        }

        // If we have metadata and are parsing, fetch initial JSON
        if (data.status === 'PPTX_PARSING' && data.json && metadata.slideCount) {
          try {
            const json = await fetchPPTJson(data.json);
            setPptJson(json);
          } catch (err) {
            console.error('Error fetching initial JSON:', err);
          }
        }

        // Handle PPTX_TRANSLATING status
        if (data.status === 'PPTX_TRANSLATING') {
          toast.loading('Translating project...', { id: 'status' });
        }

        // Handle PDF statuses
        if (data.status === 'PDF_UPLOADED') {
          toast.loading('PDF uploaded, preparing conversion...', { id: 'status' });
        }

        if (data.status === 'PDF_CONVERTING') {
          toast.loading('Converting PDF to PPTX...', { id: 'status' });
        }

        // Subscribe to real-time status changes
        unsubscribe = subscribeToProjectStatus(
          projectId,
          async (updatedData) => {
            const next = updatedData.status || '';
            const prevStatus = prevStatusRef.current;
            prevStatusRef.current = next;
            // Clear timeline immediately if entering a processing state
            if (
              next === 'PPTX_TRANSLATING' ||
              next === 'PPTX_PARSING' ||
              next === 'PDF_UPLOADING' ||
              next === 'PDF_CONVERTING'
            ) {
              setTimelineJson(undefined);
              setTimelineDetails(null);
              setTimelineError(null);
              lastTimelineUrlRef.current = null;
            }

            setProjectData(updatedData);
            setStatus(updatedData.status);

            // Track parsed slide count
            const newParsedCount = updatedData.parsedSlideCount || 0;
            const metadata = updatedData.metadata || {};

            // Set total slides from metadata
            if (metadata.slideCount) {
              setTotalSlides(metadata.slideCount);
            }

            // Check if JSON URL changed (e.g., after translation)
            const jsonUrlChanged = updatedData.json && updatedData.json !== lastJsonUrlRef.current;

            // Force refetch when transitioning from PPTX_TRANSLATING (translation complete)
            const translationJustCompleted =
              prevStatus === 'PPTX_TRANSLATING' && next !== 'PPTX_TRANSLATING';

            // Handle progressive JSON fetching OR force refetch after translation
            if (
              updatedData.json &&
              (newParsedCount > lastParsedCountRef.current ||
                translationJustCompleted ||
                jsonUrlChanged)
            ) {
              lastParsedCountRef.current = newParsedCount;
              setParsedSlideCount(newParsedCount);
              lastJsonUrlRef.current = updatedData.json;

              try {
                const json = await fetchPPTJson(updatedData.json);
                setPptJson(json);

                // Show progress toast
                if (updatedData.status === 'PPTX_PARSING') {
                  const total = metadata.slideCount || '?';
                  toast.loading(`Parsing slides... ${newParsedCount}/${total}`, { id: 'status' });
                }
              } catch (err) {
                console.error('Error fetching progressive JSON:', err);
              }
            }
            if (updatedData.timeline && updatedData.timeline !== lastTimelineUrlRef.current) {
              loadTimelineOnceAvailable(updatedData.timeline);
            }

            // Handle different statuses
            switch (updatedData.status) {
              case 'PDF_UPLOADED':
                toast.loading('PDF uploaded, preparing conversion...', { id: 'status' });
                setLoading(false);
                break;

              case 'PDF_CONVERTING':
                toast.loading('Processing file...', { id: 'status' });
                setLoading(false);
                break;

              case 'PPTX_TRANSLATING':
                toast.loading('Translating project...', { id: 'status' });
                setLoading(false);
                break;

              case 'PPTX_PARSING':
                if (!updatedData.parsedSlideCount) {
                  toast.loading('Processing...', { id: 'status' });
                }
                setLoading(false); // Allow navigation to homepage during parsing
                break;

              case 'EXTRACTION_DONE':
                if (next === 'EXTRACTION_DONE' && !updatedData.timeline) {
                  toast.loading('Processing...', { id: 'status' });
                }
                if (next === 'EXTRACTION_DONE' && updatedData.timeline) {
                  toast.success('Processing complete!', { id: 'status' });
                }
                // Fetch final PPT JSON
                if (updatedData.json) {
                  try {
                    const json = await fetchPPTJson(updatedData.json);
                    setPptJson(json);
                  } catch (err) {
                    console.error('Error fetching final JSON:', err);
                  }
                }
                setLoading(false);
                break;

              case 'ERROR': {
                const errorMessages = {
                  SLIDE_LIMIT_EXCEEDED: 'Slide limit exceeded (max allowed)',
                  UNSUPPORTED_LANGUAGE: 'Uploaded pptx language is currently not supported',
                  LANGUAGE_DETECTION_FAILED: 'Failed to detect language of uploaded pptx',
                  PPTX_PARSING_ERROR: 'Something went wrong. Try uploading a PDF instead'
                };

                const errorMsg =
                  errorMessages[updatedData.errorCode] || 'Processing error occurred';

                if (updatedData.errorCode === 'LANGUAGE_DETECTION_FAILED') {
                  toast(errorMsg, {
                    id: 'status'
                  });
                } else {
                  toast.error(errorMsg, { id: 'status' });
                }
                setError(errorMsg);
                setErrorCode(updatedData.errorCode || 'UNKNOWN_ERROR');
                setLoading(false);
                break;
              }
            }
          },
          (err) => {
            console.error('Subscription error:', err);
            setError(err.message);
            setErrorCode('UNKNOWN_ERROR');
            setLoading(false);
          }
        );

        setLoading(false);
      } catch (err) {
        console.error('Init error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    init();

    return () => {
      if (unsubscribe) unsubscribe();
      toast.dismiss('status');
    };
  }, [projectId]);

  return {
    projectData,
    pptJson,
    status,
    loading,
    error,
    errorCode,
    parsedSlideCount,
    totalSlides,
    isReady: status === 'EXTRACTION_DONE' && !!pptJson,
    isPartiallyReady:
      (status === 'PPTX_PARSING' ||
        status === 'EXTRACTION_DONE' ||
        status === 'PPTX_TRANSLATING') &&
      totalSlides > 0,
    timelineDetails,
    timelineLoading,
    timelineError,
    timelineJson,
    isTimelineReady: !!timelineDetails && !timelineError,
    reloadTimeline
  };
};
